import React from 'react';
import { THEME, shadows } from '../constants/theme';
import { StarIcon, CheckCircleIcon, ChatBubbleLeftRightIcon, MapPinIcon, ShieldCheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const TradeHub = () => {
  const features = [
    {
      icon: <MapPinIcon className="w-6 h-6" />,
      title: "Find Local Trades",
      description: "Connect with qualified tradespeople in your area instantly"
    },
    {
      icon: <StarIcon className="w-6 h-6" />,
      title: "Verified Reviews",
      description: "Read authentic reviews from real customers"
    },
    {
      icon: <ShieldCheckIcon className="w-6 h-6" />,
      title: "Trusted Professionals",
      description: "All tradespeople are verified and background-checked"
    },
    {
      icon: <ClockIcon className="w-6 h-6" />,
      title: "Quick Response",
      description: "Get quotes and book appointments within hours"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Homeowner",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330",
      content: "Found an amazing plumber through TradeHub within hours. The review system helped me make an informed decision.",
      rating: 5
    },
    {
      name: "Mike Thompson",
      role: "Licensed Electrician",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e",
      content: "TradeHub has transformed my business. The platform connects me with clients who value quality work.",
      rating: 5
    },
    {
      name: "Emma Davis",
      role: "Property Manager",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80",
      content: "Managing property maintenance has never been easier. Quick access to reliable trades makes my job so much simpler.",
      rating: 4
    }
  ];

  const tradeCategories = [
    "Plumbing",
    "Electrical",
    "Carpentry",
    "Landscaping",
    "Painting",
    "HVAC",
    "Roofing",
    "Tiling"
  ];

  return (
    <div className="min-h-screen bg-white">
        <img src='logo.png' alt="Aided Logo" className="w-40 align-right" />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1581578731548-c64695cc6952"
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Find Trusted Trades Professionals in Minutes using the Aided App!
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Connect with verified local tradespeople for your home improvement and maintenance needs.
              Read reviews, get quotes, and book appointments all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="px-8 py-4 rounded-lg font-medium text-white transition-all duration-200"
                style={{ backgroundColor: THEME.primary }}
              >
                Find a Tradesperson
              </button>
              <button
                className="px-8 py-4 rounded-lg font-medium border-2 border-white text-white transition-all duration-200 hover:bg-white hover:text-gray-900"
              >
                Join as a Professional
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ color: THEME.text }}>
              Why Choose TradeHub?
            </h2>
            <p className="text-lg" style={{ color: THEME.subtext }}>
              The smart way to connect with qualified trades professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6"
                style={{ boxShadow: shadows.md }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${THEME.primary}20`, color: THEME.primary }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: THEME.text }}>
                  {feature.title}
                </h3>
                <p style={{ color: THEME.subtext }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trade Categories */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ color: THEME.text }}>
              Available Services
            </h2>
            <p className="text-lg" style={{ color: THEME.subtext }}>
              Find the right professional for any job
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tradeCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center cursor-pointer transition-all duration-200 hover:scale-105"
                style={{ boxShadow: shadows.md }}
              >
                <h3 className="text-lg font-medium" style={{ color: THEME.text }}>
                  {category}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ color: THEME.text }}>
              What People Are Saying
            </h2>
            <p className="text-lg" style={{ color: THEME.subtext }}>
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6"
                style={{ boxShadow: shadows.md }}
              >
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-medium" style={{ color: THEME.text }}>
                      {testimonial.name}
                    </h3>
                    <p className="text-sm" style={{ color: THEME.subtext }}>
                      {testimonial.role}
                    </p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIconSolid
                      key={i}
                      className="w-5 h-5"
                      style={{ color: i < testimonial.rating ? THEME.warning : '#E5E7EB' }}
                    />
                  ))}
                </div>
                <p className="text-gray-600">{testimonial.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-r from-primary to-primary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            Ready to Get Started?
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              className="px-8 py-4 rounded-lg font-medium bg-white transition-all duration-200"
              style={{ color: THEME.primary }}
            >
              Find a Tradesperson
            </button>
            <button
              className="px-8 py-4 rounded-lg font-medium border-2 border-white text-white transition-all duration-200 hover:bg-white hover:text-primary"
            >
              Join as a Professional
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeHub; 